import { assertFileName } from '../documents/file-types.js';
import { DomainError } from '../errors.js';

/**
 * Kanıt eki (plan M7 Task 9, 10b; ADR 0029 K15). Ek değiştirilemez; raporlanmış iş emrinin eki
 * tek başına iptal edilemez ve iş emrine yeni ek eklenemez [NR496 4.5.3f]. Dosyanın kendisi M7b2'de
 * gemiden buluta gider; satır dosyanın SHA-256'sını ve boyutunu taşır. Saf modül.
 */

/** Sahip türü kapalı listesi (plan Task 9): arıza M7c'de, sertifika kayıtları M7d'de. */
export const ATTACHMENT_OWNER_TYPES = [
  'work_order',
  // Raporlanmış kaydın sonradan gelen CM tanı/öngörü raporu (ADR 0029 Ek M7f F4) [NR496 4.5.4e]
  'work_record',
  'defect',
  'certificate_endorsement',
  'class_confirmation',
  'certificate_issuance',
  // HSE raporu ve faaliyetinin fotoğraf, tutanak ve kanıt dosyaları (ADR 0049 §2)
  'hse_report',
  'hse_action',
  // Günlük raporun ekleri (ADR 0051 §7)
  'daily_report',
  // Liman aramasının belgeleri (M13f; ADR 0052 §2)
  'port_call',
  // Sefer raporunun (olay, Noon) fotoğraf ve belgeleri (ADR 0057 §3)
  'voyage_report',
  // Bordro ödemesinin dekontu (M16; ADR 0061 §1)
  'payroll_payment',
  // Yakıt tankının kalibrasyon ve trim/list tablosu (ADR 0117 §4)
  'fuel_tank',
  // Yakıt alımının ve hareketinin belgeleri (ADR 0117 Ek Y2)
  'fuel_bunkering',
  'fuel_operation',
  // ROB düzeltme talebinin dayanak belgesi (ADR 0117 Ek Y4)
  'fuel_adjustment',
  // RA bulgusunun kanıt dosyası (fotoğraf, test kaydı) ve kasa hareketinin makbuzu (ADR 0121;
  // sorular #979, #966)
  'ra_finding',
  'cash_transaction',
  // Tatbikat adımının fotoğraf ve belge kanıtı (ADR 0128 Faz 3 Ek)
  'drill_record_step',
  // Pre-Docking denetim sonucunun fotoğraf, çizim, rapor ve ölçüm dosyası (ADR 0129 Ek Faz 2)
  'pdi_result',
] as const;
export type AttachmentOwnerType = (typeof ATTACHMENT_OWNER_TYPES)[number];

export const ATTACHMENT_KINDS = ['photo', 'report', 'other'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

/** Dosya başına en çok 25 MB (K15). */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Kabul edilen türler: JPG, PNG, PDF (K15). Fotoğraf gemide JPEG'e küçültülür (§6.9). */
export const ATTACHMENT_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const;

export type AttachmentMimeType = (typeof ATTACHMENT_MIME_TYPES)[number];

/** İçeriğin ilk baytları: JPEG `FF D8 FF`, PNG imzası, PDF `%PDF-`. */
const SIGNATURES: readonly (readonly [AttachmentMimeType, readonly number[]])[] = [
  ['image/jpeg', [0xff, 0xd8, 0xff]],
  ['image/png', [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  ['application/pdf', [0x25, 0x50, 0x44, 0x46, 0x2d]],
];

/**
 * Ekin türü içerikten (K15): istemcinin bildirdiği tür ya da dosya adı güvenilmez. Masaüstü ana
 * süreci, gemi sunucusu ve bulut doğrulaması aynı denetimi yapar. Tanınmazsa `null`.
 */
export function sniffAttachmentType(head: Uint8Array): AttachmentMimeType | null {
  for (const [type, signature] of SIGNATURES) {
    if (head.length >= signature.length && signature.every((byte, i) => head[i] === byte)) {
      return type;
    }
  }
  return null;
}

const SHA256 = /^[0-9a-f]{64}$/;
const MAX_CAPTION = 500;

export interface AttachmentInput {
  ownerType: string;
  kind: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  sha256: string;
  caption: string | null;
}

/** Ekin biçimi: sahip türü, tür, dosya adı, boyut, MIME türü, SHA-256 ve açıklama. */
export function assertAttachment(input: AttachmentInput): void {
  if (!(ATTACHMENT_OWNER_TYPES as readonly string[]).includes(input.ownerType)) {
    throw new DomainError('INVALID_OWNER_TYPE', String(input.ownerType));
  }
  if (!(ATTACHMENT_KINDS as readonly string[]).includes(input.kind)) {
    throw new DomainError('INVALID_ATTACHMENT', `kind:${String(input.kind)}`);
  }
  assertFileName(typeof input.fileName === 'string' ? input.fileName : '');
  if (!Number.isInteger(input.fileSize) || input.fileSize < 1) {
    throw new DomainError('INVALID_ATTACHMENT', `size:${String(input.fileSize)}`);
  }
  if (input.fileSize > MAX_ATTACHMENT_BYTES) {
    throw new DomainError('FILE_TOO_LARGE', String(input.fileSize));
  }
  if (!(ATTACHMENT_MIME_TYPES as readonly string[]).includes(input.mimeType)) {
    throw new DomainError('UNSUPPORTED_FILE_TYPE', String(input.mimeType));
  }
  if (typeof input.sha256 !== 'string' || !SHA256.test(input.sha256)) {
    throw new DomainError('INVALID_SHA256', String(input.sha256));
  }
  if (
    input.caption !== null &&
    (typeof input.caption !== 'string' || input.caption.length > MAX_CAPTION)
  ) {
    throw new DomainError('INVALID_ATTACHMENT', 'caption');
  }
}
